import { AnalyticsService } from '@alison/analytics/client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getSelectionCookie, hasSelectionCookie, setSelectionCookie } from '../../lib/cookies';
import { useClients } from '../../lib/filters.hooks';
import { DATE_PRESETS, DEFAULT_SCOPE } from './constants';
import type { DateRangeValue, ScopeState } from './types';

interface ScopeAccount {
  id: number;
  productId: number;
  network: string | null;
  networkLabel: string;
}

interface ScopeClient {
  id: number;
  accounts: ScopeAccount[];
}

type ScopeSelection = Pick<ScopeState, 'brand' | 'platform' | 'account'>;

interface UseScopeSelectionOptions {
  scope?: ScopeState;
  onScopeChange?: (scope: ScopeState) => void;
  urlHasScope?: boolean;
}

function rangeLabel(range: DateRangeValue): string {
  if (range.mode === 'custom') return 'Custom';
  return DATE_PRESETS.find((option) => option.id === range.preset)?.label ?? range.preset;
}

function customRangeDates(range: DateRangeValue): { rangeFrom?: string; rangeTo?: string } {
  if (range.mode !== 'custom') return {};
  return {
    ...(range.from !== '' && { rangeFrom: range.from }),
    ...(range.to !== '' && { rangeTo: range.to }),
  };
}

function networksOf(accounts: ScopeAccount[]): string[] {
  return [
    ...new Set(accounts.flatMap((account) => (account.network === null ? [] : [account.network]))),
  ];
}

// "brand" is a product; narrows client accounts by product → platform → account.
function scopedAccounts(clients: ScopeClient[], scope: ScopeSelection): ScopeAccount[] {
  const allAccounts = clients.flatMap((client) => client.accounts);
  const brandAccounts = scope.brand.length
    ? allAccounts.filter((account) => scope.brand.includes(String(account.productId)))
    : allAccounts;
  const networkAccounts = scope.platform.length
    ? brandAccounts.filter(
        (account) => account.network !== null && scope.platform.includes(account.network),
      )
    : brandAccounts;
  // Picks left over from another platform or brand are ignored: an empty scope would
  // send no accountIds, and the run would fall back to every entitled account.
  const picked = networkAccounts.filter((account) => scope.account.includes(String(account.id)));
  return picked.length ? picked : networkAccounts;
}

function scopeFromSelection(clients: ScopeClient[]): ScopeSelection {
  const selected = new Set(getSelectionCookie());
  const allAccounts = clients.flatMap((client) => client.accounts);
  const products = [...new Set(allAccounts.map((account) => account.productId))];
  const selectedProducts = [
    ...new Set(allAccounts.filter((account) => selected.has(account.id)).map((a) => a.productId)),
  ];
  const brand = selectedProducts.length === products.length ? [] : selectedProducts.map(String);
  const brandAccounts = allAccounts.filter(
    (account) => !brand.length || brand.includes(String(account.productId)),
  );
  const selectedNetworks = networksOf(brandAccounts.filter((account) => selected.has(account.id)));
  const platform =
    selectedNetworks.length === networksOf(brandAccounts).length ? [] : selectedNetworks;
  const platformAccounts = brandAccounts.filter(
    (account) =>
      !platform.length || (account.network !== null && platform.includes(account.network)),
  );
  const chosen = platformAccounts.filter((account) => selected.has(account.id));
  const account =
    chosen.length === platformAccounts.length ? [] : chosen.map((entry) => String(entry.id));
  return { brand, platform, account };
}

// Side-effect-free scope-derived data — safe to call alongside useScopeSelection without
// duplicating its cookie/analytics/query-invalidation effects.
export function useScopeAccounts(scope: ScopeState) {
  const { data: clients } = useClients();

  const networkLabels = useMemo(
    () =>
      Object.fromEntries(
        (clients ?? [])
          .flatMap((client) => client.accounts)
          .filter((account) => account.network !== null)
          .map((account) => [account.network as string, account.networkLabel]),
      ),
    [clients],
  );

  const accountIds = useMemo(
    () => scopedAccounts(clients ?? [], scope).map((account) => account.id),
    [clients, scope],
  );

  const scopeNetworks = useMemo(
    () => networksOf(scopedAccounts(clients ?? [], scope)),
    [clients, scope],
  );

  return { clients, networkLabels, accountIds, scopeNetworks };
}

export function useScopeSelection(options?: UseScopeSelectionOptions) {
  const [internalScope, setInternalScope] = useState<ScopeState>(DEFAULT_SCOPE);
  const controlled = options?.scope !== undefined && options?.onScopeChange !== undefined;
  const scope = controlled && options ? (options.scope ?? DEFAULT_SCOPE) : internalScope;
  const setScope = controlled && options?.onScopeChange ? options.onScopeChange : setInternalScope;
  const queryClient = useQueryClient();
  const { clients, networkLabels, accountIds, scopeNetworks } = useScopeAccounts(scope);

  useEffect(() => {
    const fallback = clients?.[0];
    if (!fallback || hasSelectionCookie()) return;
    setSelectionCookie(fallback.accounts.map((account) => account.id));
    void queryClient.invalidateQueries();
  }, [clients, queryClient]);

  const scopeHydrated = useRef(false);
  useEffect(() => {
    if (scopeHydrated.current || !clients?.length || !hasSelectionCookie() || options?.urlHasScope)
      return;
    scopeHydrated.current = true;
    setScope({ ...scope, ...scopeFromSelection(clients) });
  }, [clients, scope, options?.urlHasScope, setScope]);

  const applyScope = (next: ScopeState): void => {
    setScope(next);
    const accounts = scopedAccounts(clients ?? [], next);
    const brandAccounts = scopedAccounts(clients ?? [], { ...next, platform: [], account: [] });
    setSelectionCookie((accounts.length ? accounts : brandAccounts).map((account) => account.id));
    const allAccounts = (clients ?? []).flatMap((client) => client.accounts);
    const productNames = new Map(
      allAccounts.map((account) => [String(account.productId), account.productName]),
    );
    const accountNames = new Map(allAccounts.map((account) => [String(account.id), account.name]));
    AnalyticsService.track('Filter - Scope Changed', {
      brands: next.brand.map((id) => productNames.get(id) ?? id),
      platforms: next.platform.map((key) => networkLabels[key] ?? key),
      accounts: next.account.map((id) => accountNames.get(id) ?? id),
      creativeTypes: next.creativeType,
      rangePreset: rangeLabel(next.range),
      ...customRangeDates(next.range),
    });
    void queryClient.invalidateQueries();
  };

  useEffect(() => {
    AnalyticsService.register({ accountIds });
  }, [accountIds]);

  return { scope, applyScope, scopeNetworks, networkLabels, accountIds };
}
